import React, { useMemo, useState } from 'react';
import { Badge, Popover, List, Button, Spin, Typography, Space, Tooltip, Checkbox, Modal } from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useTaskStore from '../store/taskStore';
import useIsMobile from '../hooks/useIsMobile';
import type { Task } from '../api/tasks';

const POPOVER_WIDTH_COLLAPSED = 360;
const POPOVER_WIDTH_EXPANDED = 520;

const styles = {
  popoverContent: {
    maxHeight: 400,
    overflowY: 'auto' as const,
    transition: 'width 0.2s ease',
  },
  bellButton: {
    fontSize: 18,
    cursor: 'pointer',
    color: '#595959',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
  },
  unreadItem: {
    backgroundColor: '#e6f7ff',
  },
  actionNeededItem: {
    backgroundColor: '#fff7e6',
    borderLeft: '3px solid #faad14',
  },
  readItem: {
    backgroundColor: 'transparent',
  },
  footer: {
    textAlign: 'center' as const,
    padding: '8px 0 4px',
    borderTop: '1px solid #f0f0f0',
  },
  emptyText: {
    textAlign: 'center' as const,
    padding: '24px 0',
    color: '#8c8c8c',
  },
  listItem: {
    padding: '10px 12px',
    cursor: 'pointer',
  },
  detailText: {
    whiteSpace: 'pre-wrap' as const,
    marginBottom: 0,
  },
};

function needsAction(task: Task): boolean {
  return task.status === 'done' && !!task.result_json?.action_label;
}

function statusIcon(task: Task) {
  if (needsAction(task)) {
    return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
  }
  if (task.type === 'print_job') {
    return <PrinterOutlined style={{ color: '#1890ff' }} />;
  }
  switch (task.status) {
    case 'pending':
      return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
    case 'running':
      return <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />;
    case 'done':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'error':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    default:
      return null;
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин. назад`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ч. назад`;
  const days = Math.floor(hours / 24);
  return `${days} дн. назад`;
}

function getTaskDetailText(task: Task): string {
  if (task.error_message) {
    return task.error_message;
  }

  const result = task.result_json;
  if (!result) {
    return '';
  }

  if (typeof result.message === 'string' && result.message.trim()) {
    return result.message;
  }

  return JSON.stringify(result, null, 2);
}

const TaskBell: React.FC = () => {
  const {
    tasks,
    unreadCount,
    _dropdownOpen,
    setDropdownOpen,
    markSeen,
    markAllSeen,
    dismissTask,
  } = useTaskStore();

  const successCount = useMemo(
    () => tasks.filter(t => t.status === 'done' && !t.seen_at && !needsAction(t)).length,
    [tasks],
  );
  const attentionCount = useMemo(
    () => tasks.filter(t => !t.seen_at && (t.status === 'error' || needsAction(t))).length,
    [tasks],
  );
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  /** Первый клик по карточке с обрезанным заголовком — раскрывает её.
   *  Второй клик (или клик когда заголовок целиком влезает) — выполняет действие. */
  const guardClick = (task: Task, run: () => void) => (e: React.MouseEvent) => {
    if (expandedId === task.id) {
      run();
      return;
    }
    const row = (e.currentTarget as HTMLElement).closest('.ant-list-item') as HTMLElement | null;
    const titleEl = row?.querySelector<HTMLElement>('[data-task-title]');
    const overflows = titleEl ? titleEl.scrollWidth > titleEl.clientWidth + 1 : false;
    if (overflows) {
      setExpandedId(task.id);
      return;
    }
    run();
  };

  const selectedTaskDetail = useMemo(
    () => (selectedTask ? getTaskDetailText(selectedTask) : ''),
    [selectedTask],
  );

  const handleTaskClick = (task: Task) => {
    if (!task.seen_at) {
      markSeen(task.id);
    }

    if (task.type === 'unclassified_products' && task.result_json?.link) {
      setDropdownOpen(false);
      navigate(task.result_json.link as string);
      return;
    }

    if (task.type === 'cart_price_changes') {
      setDropdownOpen(false);
      navigate('/oil-orders/cart?cart_validation=1');
      return;
    }

    if (task.type === 'employee_birthday') {
      const empId = task.result_json?.employee_id;
      setDropdownOpen(false);
      navigate(empId ? `/employees?highlight=${empId}` : '/employees');
      return;
    }

    if (task.error_message || task.result_json?.message) {
      setSelectedTask(task);
    }

    if (task.status === 'done' && task.result_json?.neva_cart_url) {
      const token = localStorage.getItem('access_token') || '';
      window.open(`/api/oil-orders/neva-redirect?token=${encodeURIComponent(token)}`, '_blank');
    }
  };

  const popoverWidth: number | string = isMobile
    ? 'calc(100vw - 24px)'
    : (expandedId !== null ? POPOVER_WIDTH_EXPANDED : POPOVER_WIDTH_COLLAPSED);

  const content = (
    <div style={{ ...styles.popoverContent, width: popoverWidth }}>
      {tasks.length === 0 ? (
        <div style={styles.emptyText}>
          <Typography.Text type="secondary">Нет задач</Typography.Text>
        </div>
      ) : (
        <>
          <List<Task>
            dataSource={tasks}
            renderItem={(task: Task) => {
              const isUnread = task.seen_at === null;
              const hasAction = needsAction(task);
              const isTerminal = task.status === 'done' || task.status === 'error';
              const isInfo = isTerminal && !hasAction;
              const bgStyle = hasAction
                ? styles.actionNeededItem
                : isUnread ? styles.unreadItem : styles.readItem;

              const isExpanded = expandedId === task.id;
              const titleStyle: React.CSSProperties = {
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontWeight: isUnread ? 600 : 400,
                display: 'block',
                whiteSpace: isExpanded ? 'normal' : 'nowrap',
                overflow: 'hidden',
                textOverflow: isExpanded ? 'clip' : 'ellipsis',
                wordBreak: isExpanded ? 'break-word' : 'normal',
              };

              const runActionButton = () => {
                if (task.type === 'cart_price_changes') {
                  setDropdownOpen(false);
                  navigate('/oil-orders/cart?cart_validation=1');
                } else if (task.type === 'employee_birthday') {
                  setDropdownOpen(false);
                  const empId = task.result_json?.employee_id;
                  navigate(empId ? `/employees?highlight=${empId}` : '/employees');
                } else if (task.result_json?.neva_cart_url) {
                  const tk = localStorage.getItem('access_token') || '';
                  window.open(`/api/oil-orders/neva-redirect?token=${encodeURIComponent(tk)}`, '_blank');
                }
                if (!task.seen_at) {
                  markSeen(task.id);
                }
              };

              return (
                <List.Item
                  style={{
                    ...styles.listItem,
                    ...bgStyle,
                  }}
                  onClick={guardClick(task, () => handleTaskClick(task))}
                >
                  {hasAction ? (
                    <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, paddingTop: 2 }}>
                        {statusIcon(task)}
                        {!task.seen_at && (
                          <Tooltip title="Отметить как обработанное">
                            <Checkbox
                              checked={false}
                              onClick={(e) => {
                                e.stopPropagation();
                                markSeen(task.id);
                              }}
                              style={{ marginTop: 2 }}
                            />
                          </Tooltip>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, width: '100%' }}>
                          <span data-task-title style={titleStyle}>
                            {task.title}
                          </span>
                          <Space size={4} style={{ flexShrink: 0, marginTop: 1 }}>
                            <Tooltip title={new Date(task.created_at).toLocaleString('ru-RU')}>
                              <Typography.Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                {timeAgo(task.created_at)}
                              </Typography.Text>
                            </Tooltip>
                            {task.seen_at && (
                              <Tooltip title="Убрать">
                                <CloseOutlined
                                  style={{ fontSize: 12, color: '#8c8c8c', cursor: 'pointer' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dismissTask(task.id);
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Space>
                        </div>
                        <Button
                          type="link"
                          size="small"
                          style={{ color: '#fa8c16', fontWeight: 500, padding: '0 4px', marginTop: 2 }}
                          onClick={guardClick(task, runActionButton)}
                        >
                          {task.result_json?.action_label}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                      <span style={{ flexShrink: 0, marginTop: 1 }}>{statusIcon(task)}</span>
                      <span data-task-title style={titleStyle}>
                        {task.title}
                      </span>
                      <Tooltip title={new Date(task.created_at).toLocaleString('ru-RU')}>
                        <Typography.Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0, marginTop: 1 }}>
                          {timeAgo(task.created_at)}
                        </Typography.Text>
                      </Tooltip>
                      {isInfo && (
                        <Tooltip title="Убрать">
                          <CloseOutlined
                            style={{ fontSize: 12, color: '#8c8c8c', cursor: 'pointer', flexShrink: 0, marginTop: 4 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissTask(task.id);
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  )}
                </List.Item>
              );
            }}
          />
          {unreadCount > 0 && (
            <div style={styles.footer}>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  markAllSeen();
                }}
              >
                Прочитать все
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        title="Задачи"
        trigger="click"
        placement="bottomRight"
        open={_dropdownOpen}
        onOpenChange={(open) => {
          setDropdownOpen(open);
          if (!open) setExpandedId(null);
        }}
      >
        <span style={styles.bellButton}>
          <span style={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: attentionCount > 0 ? '#fff7e6' : 'transparent',
            transition: 'background 0.3s',
          }}>
            <BellOutlined style={{ fontSize: 18, color: attentionCount > 0 ? '#fa8c16' : '#595959', transition: 'color 0.3s' }} />
            {(successCount > 0 || attentionCount > 0) && (
              <span style={{
                position: 'absolute',
                top: -6,
                right: 0,
                transform: 'translateX(50%)',
                display: 'flex',
                gap: 2,
              }}>
                {attentionCount > 0 && (
                  <span style={{
                    background: '#ff4d4f',
                    color: '#fff',
                    fontSize: 10,
                    lineHeight: '16px',
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    textAlign: 'center',
                    padding: '0 4px',
                    fontWeight: 600,
                  }}>
                    {attentionCount}
                  </span>
                )}
                {successCount > 0 && (
                  <span style={{
                    background: '#52c41a',
                    color: '#fff',
                    fontSize: 10,
                    lineHeight: '16px',
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    textAlign: 'center',
                    padding: '0 4px',
                    fontWeight: 600,
                  }}>
                    {successCount}
                  </span>
                )}
              </span>
            )}
          </span>
        </span>
      </Popover>
      <Modal
        open={!!selectedTask}
        title={selectedTask?.title || 'Детали задачи'}
        footer={null}
        onCancel={() => setSelectedTask(null)}
        width={720}
      >
        {selectedTask && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              Статус: {selectedTask.status}
            </Typography.Text>
            <Typography.Paragraph style={styles.detailText}>
              {selectedTaskDetail || 'Подробности отсутствуют'}
            </Typography.Paragraph>
          </Space>
        )}
      </Modal>
    </>
  );
};

export default TaskBell;
